import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  VIEW_PREFERENCE_SCHEMAS,
  type UserPreferencesResponse,
  type ViewKey,
  type ViewPreferenceFor,
} from '@vagusx/clients';
import { getUserPreferencesClient } from '../lib/api-clients';
import { queryKeys } from '../lib/query-keys';

/**
 * Hydrate the signed-in user's UI preferences once per session. Every
 * view reads its own slice via `useGetViewPreference(viewKey)` below —
 * this hook is the shared data source.
 *
 * Returns `{ preferences: {} }` until the GET resolves; viewers should
 * treat an absent view_key as "no saved state, use defaults" rather
 * than gating behind loading.
 */
export function useUserPreferences() {
  return useQuery({
    queryKey: queryKeys.userPreferences.all,
    queryFn: () => getUserPreferencesClient().getPreferences(),
    // Preferences change rarely during a session (only when THIS user
    // clicks a filter), and every mutation below writes directly into
    // the cache. 10-minute staleTime is a comfortable balance — fresh
    // enough to pick up writes from another device on page refresh,
    // infrequent enough to avoid chatter.
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

/**
 * Typed accessor for a single view's preferences. Validates the stored
 * payload against the view's registered Zod schema before returning —
 * if the shape doesn't match (older frontend wrote a different shape,
 * DB manipulation, etc.), returns `undefined` so the caller falls back
 * to defaults instead of crashing on an unchecked cast.
 */
export function useGetViewPreference<K extends ViewKey>(
  viewKey: K
): { data: ViewPreferenceFor<K> | undefined; isLoading: boolean } {
  const { data, isLoading } = useUserPreferences();

  const parsed = useMemo((): ViewPreferenceFor<K> | undefined => {
    const raw = data?.preferences[viewKey];
    if (!raw) return undefined;
    const schema = VIEW_PREFERENCE_SCHEMAS[viewKey];
    const result = schema.safeParse(raw);
    return result.success ? (result.data as ViewPreferenceFor<K>) : undefined;
  }, [data, viewKey]);

  return { data: parsed, isLoading };
}

/**
 * Save (replace) a single view's preferences. Optimistically updates
 * the cached blob so sibling writers for OTHER view_keys don't see
 * stale siblings while this write is in flight.
 *
 * The server performs an atomic partial upsert (INSERT ... ON CONFLICT
 * DO UPDATE with jsonb_set) — two tabs writing DIFFERENT view_keys
 * never lose updates; same-view concurrent writes are last-write-wins.
 *
 * Most callers want the view-bound variant `useSavePreferenceFor(key)`
 * below; this is the raw hook for code that chooses the view_key at
 * call time.
 */
export function useSaveViewPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ viewKey, payload }: { viewKey: ViewKey; payload: Record<string, unknown> }) =>
      getUserPreferencesClient().upsertView(viewKey, payload),
    onMutate: async ({ viewKey, payload }) => {
      // Optimistic: overwrite the view slice immediately so the UI
      // reflects the save before the round-trip completes.
      await qc.cancelQueries({ queryKey: queryKeys.userPreferences.all });
      const prior = qc.getQueryData<UserPreferencesResponse>(queryKeys.userPreferences.all);
      const next: UserPreferencesResponse = {
        preferences: { ...(prior?.preferences ?? {}), [viewKey]: payload },
      };
      qc.setQueryData(queryKeys.userPreferences.all, next);
      return { prior };
    },
    onError: (_err, _vars, context) => {
      // Roll back if the PATCH fails. The filter bar's UI state is
      // already URL-driven, so a silent rollback just means "your
      // filters still WORK this session but won't be remembered next
      // time" — a reasonable degraded mode.
      if (context?.prior) {
        qc.setQueryData(queryKeys.userPreferences.all, context.prior);
      }
    },
    onSuccess: (res) => {
      // Server returns the full post-write blob — replace the cache
      // with it so any optimistic drift is corrected in one step.
      qc.setQueryData(queryKeys.userPreferences.all, res);
    },
  });
}

/**
 * Remove a view_key entirely from the user's preferences (not "set to
 * empty" — the key itself is dropped). Optimistically updates the
 * cache; rolls back on error. Use the view-bound `useDeletePreferenceFor(key)`
 * below for ergonomic consumer code.
 */
export function useDeleteViewPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (viewKey: ViewKey) => getUserPreferencesClient().deleteView(viewKey),
    onMutate: async (viewKey) => {
      await qc.cancelQueries({ queryKey: queryKeys.userPreferences.all });
      const prior = qc.getQueryData<UserPreferencesResponse>(queryKeys.userPreferences.all);
      if (prior) {
        const { [viewKey]: _removed, ...rest } = prior.preferences;
        qc.setQueryData(queryKeys.userPreferences.all, { preferences: rest });
      }
      return { prior };
    },
    onError: (_err, _viewKey, context) => {
      if (context?.prior) {
        qc.setQueryData(queryKeys.userPreferences.all, context.prior);
      }
    },
    onSuccess: (res) => {
      qc.setQueryData(queryKeys.userPreferences.all, res);
    },
  });
}

// ---------------------------------------------------------------------------
// View-bound convenience hooks — the recommended consumer API.
//
// Consumers that know their view_key at mount time pre-bind it here so
// calls become `save.mutate(payload)` / `remove.mutate()` instead of
// `save.mutate({ viewKey, payload })`. Type parameter flows through so
// the payload is checked against the registered Zod schema's shape.
// ---------------------------------------------------------------------------

/** Save preference hook bound to a specific view_key. */
export function useSavePreferenceFor<K extends ViewKey>(viewKey: K) {
  const save = useSaveViewPreference();
  return {
    mutate: (payload: ViewPreferenceFor<K>) =>
      save.mutate({ viewKey, payload: payload as Record<string, unknown> }),
    mutateAsync: (payload: ViewPreferenceFor<K>) =>
      save.mutateAsync({ viewKey, payload: payload as Record<string, unknown> }),
    isPending: save.isPending,
    isError: save.isError,
    error: save.error,
  };
}

/** Delete preference hook bound to a specific view_key. */
export function useDeletePreferenceFor<K extends ViewKey>(viewKey: K) {
  const del = useDeleteViewPreference();
  return {
    mutate: () => del.mutate(viewKey),
    mutateAsync: () => del.mutateAsync(viewKey),
    isPending: del.isPending,
    isError: del.isError,
    error: del.error,
  };
}
